<template>
  <section class="field-selection min-w-0 pb-10">
    <span class="text-black font-semibold flex items-center">
      FIELD SELECTION
    </span>
    <p class="text-gray-dark text-sm mt-1">
      Choose how fields from the first <code class="text-black">&lt;{{ itemTag || 'item' }}&gt;</code> item are used.
    </p>
    <p class="text-gray-dark text-sm mt-1">
      {{ payloadFieldCount }} Alias · {{ embedFields.length }} Search · {{ groupKey.length }} Group key
    </p>

    <label class="field-filter mt-3" aria-label="Filter fields">
      <vs-icon icon="search" class="field-filter__icon text-gray"/>
      <input v-model.trim="filter"
             class="field-filter__input"
             type="search"
             placeholder="Filter fields"
      />
    </label>

    <div class="field-table mt-3" role="table" aria-label="XML feed field selection">
      <div class="field-table__header" role="row">
        <span role="columnheader" class="field-table__field-heading">Field &amp; example</span>
        <span role="columnheader" class="field-table__input-heading"></span>
        <span role="columnheader" class="field-table__role-heading">
          Alias
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="aliasHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span role="columnheader" class="field-table__role-heading">
          Search
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="searchHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span role="columnheader" class="field-table__role-heading">
          Group
          <vs-tooltip class="field-table__hint" position="left">
            <template #content>
              <div v-html="groupKeyHint"/>
            </template>
            <vs-icon icon="info" class="field-table__info-icon text-gray cursor-default"/>
          </vs-tooltip>
        </span>
        <span class="field-table__line" aria-hidden="true"/>
      </div>

      <div class="field-table__body">
        <div v-for="tag in filteredTags" :key="tag" class="field-table__row" role="row">
          <div class="field-table__field" role="cell">
            <span class="field-table__key font-mono text-sm" :title="tag">{{ tag }}</span>
          </div>

          <div class="field-table__alias" role="cell">
            <template v-if="isPayload(tag)">
              <vs-icon icon="arrow_forward" class="field-table__alias-arrow text-gray"/>
              <input :value="payloadFields[tag].as"
                     :aria-label="`Save ${tag} as`"
                     data-testid="payload-alias"
                     class="field-table__alias-input"
                     type="text"
                     @input="setPayloadProp(tag, 'as', $event.target.value)"
              />
            </template>
          </div>

          <label class="field-table__checkbox" :aria-label="`Save ${tag} as an alias`" role="cell">
            <input type="checkbox"
                   :checked="isPayload(tag)"
                   @change="togglePayload(tag, $event.target.checked)"
            />
          </label>

          <label class="field-table__checkbox" :aria-label="`Use ${tag} for search`" role="cell">
            <input type="checkbox"
                   :checked="isEmbed(tag)"
                   @change="toggleEmbed(tag, $event.target.checked)"
            />
          </label>

          <label class="field-table__checkbox" :aria-label="`Use ${tag} as group key`" role="cell">
            <input type="checkbox"
                   :checked="isGroupKey(tag)"
                   @change="toggleGroupKey(tag, $event.target.checked)"
            />
          </label>

          <span v-if="fieldExample(tag)"
                class="field-table__example text-gray text-xs truncate"
                role="cell"
                :title="fieldExample(tag)"
          >{{ fieldExample(tag) }}</span>
          <span class="field-table__line" aria-hidden="true"/>
        </div>

        <p v-if="availableTags.length && !filteredTags.length" class="field-table__empty text-gray text-sm py-4 text-center">
          No matching fields
        </p>
      </div>
    </div>
  </section>
</template>

<script>
import equals from 'ramda/src/equals'
import without from 'ramda/src/without'
import UserProfileService from '@/services/amio/user-profile.service'

function defaultPayloadName(tag) {
  return tag.replace(/^[a-zA-Z]+:/, '').replace(/[^a-zA-Z0-9_]/g, '_')
}

export default {
  name: 'SettingsIndexFieldSelection',
  props: {
    availableTags: {
      type: Array,
      default: () => []
    },
    fieldExamples: {
      type: Object,
      default: () => ({})
    },
    itemTag: {
      type: String,
      default: ''
    },
    value: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    const value = this.value || {}
    return {
      filter: '',
      embedFields: [...(value.embed_fields || [])],
      payloadFields: { ...(value.payload_fields || {}) },
      groupKey: [...(value.group_key || [])]
    }
  },
  computed: {
    searchHint() {
      return 'When checked, this field is embedded for semantic search.'
    },
    groupKeyHint() {
      return 'When checked, this field is the group key that lets size/variant rows reuse one embedding.'
    },
    indexingHint() {
      return UserProfileService.isAdmin() ?
        'Which payload fields are indexed is set per collection in Settings → Vector Collections.' :
        'Which payload fields are indexed is managed at the collection level by an administrator.'
    },
    aliasHint() {
      return `When checked, this field is kept in the payload for filters and card display. Use the input to store it under a different name.
        <br/><br/>
        ${this.indexingHint}`
    },
    filteredTags() {
      const query = this.filter.toLowerCase()
      return [...this.availableTags]
        .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
        .filter(tag => !query ||
          tag.toLowerCase().includes(query) ||
          this.fieldExample(tag).toLowerCase().includes(query))
    },
    payloadFieldCount() {
      return Object.keys(this.payloadFields).length
    },
    config() {
      return {
        embed_fields: this.embedFields,
        payload_fields: this.payloadFields,
        group_key: this.groupKey
      }
    }
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        const nextConfig = {
          embed_fields: [...((value && value.embed_fields) || [])],
          payload_fields: { ...((value && value.payload_fields) || {}) },
          group_key: [...((value && value.group_key) || [])]
        }
        if (equals(nextConfig, this.config)) return

        this.embedFields = nextConfig.embed_fields
        this.payloadFields = nextConfig.payload_fields
        this.groupKey = nextConfig.group_key
      }
    },
    config: {
      deep: true,
      handler(config) {
        this.$emit('input', config)
      }
    }
  },
  methods: {
    fieldExample(tag) {
      const value = this.fieldExamples[tag]
      if (value === null || value === undefined) return ''
      return typeof value === 'object' ? JSON.stringify(value) : String(value)
    },
    isEmbed(tag) {
      return this.embedFields.includes(tag)
    },
    isPayload(tag) {
      return Object.prototype.hasOwnProperty.call(this.payloadFields, tag)
    },
    isGroupKey(tag) {
      return this.groupKey.includes(tag)
    },
    toggleEmbed(tag, on) {
      this.embedFields = on ? [...this.embedFields, tag] : without([tag], this.embedFields)
    },
    toggleGroupKey(tag, on) {
      this.groupKey = on ? [...this.groupKey, tag] : without([tag], this.groupKey)
    },
    togglePayload(tag, on) {
      const payloadFields = { ...this.payloadFields }
      if (on) {
        payloadFields[tag] = { as: defaultPayloadName(tag) }
      } else {
        delete payloadFields[tag]
      }
      this.payloadFields = payloadFields
    },
    setPayloadProp(tag, prop, value) {
      this.payloadFields = {
        ...this.payloadFields,
        [tag]: { ...this.payloadFields[tag], [prop]: value }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.field-filter {
  align-items: center;
  border: 1px solid #DCE1EA;
  border-radius: 4px;
  display: flex;
  height: 36px;
  max-width: 220px;
  padding: 0 10px;

  &:focus-within {
    border-color: #7E50FF;
  }
}

.field-filter__icon {
  font-size: 18px;
  margin-right: 7px;
}

.field-filter__input {
  background: transparent;
  border: 0;
  color: inherit;
  min-width: 0;
  outline: 0;
  width: 100%;
}

.field-table {
  --field-table-columns: minmax(0, 1fr) calc(8rem + 10px) 3.75rem 3.75rem 3.75rem;
  font-size: 12px;
  min-width: 0;
  width: 100%;
}

.field-table__header,
.field-table__body {
  column-gap: 8px;
  display: grid;
  grid-template-columns: var(--field-table-columns);
}

.field-table__header {
  background: #fff;
  position: sticky;
  top: 0;
  z-index: 2;
}

.field-table__row {
  display: contents;
}

.field-table__field-heading,
.field-table__input-heading,
.field-table__role-heading {
  align-items: center;
  color: #66748E;
  display: flex;
  font-weight: 500;
  min-height: 32px;
}

.field-table__field-heading {
  white-space: nowrap;
}

.field-table__role-heading {
  font-size: 11px;
  gap: 2px;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}

.field-table__hint {
  display: inline-flex;
  flex-shrink: 0;
}

.field-table__info-icon {
  font-size: 14px;
}

.field-table__line {
  border-bottom: 1px solid #DCE1EA;
  grid-column: 1 / -1;
  height: 0;
  margin: 4px 0;
}

.field-table__empty {
  grid-column: 1 / -1;
}

.field-table__field {
  min-width: 0;
}

.field-table__key {
  align-items: center;
  display: block;
  height: 25px;
  line-height: 25px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-table__example {
  grid-column: 1 / 3;
  margin-top: 2px;
  min-width: 0;
}

.field-table__checkbox {
  align-items: center;
  display: flex;
  height: 25px;
  justify-content: center;
  justify-self: stretch;
  width: 100%;
}

.field-table__alias {
  align-items: center;
  display: flex;
  gap: 4px;
  height: 25px;
}

.field-table__alias-arrow {
  flex-shrink: 0;
  font-size: 14px;
}

.field-table__alias-input {
  background: white;
  border: 1px solid #DCE1EA;
  border-radius: 3px;
  flex: 1;
  font-size: 11px;
  height: 25px;
  min-width: 0;
  padding: 0 6px;
  width: 100%;

  &:focus {
    border-color: #7E50FF;
    outline: 0;
  }
}
</style>
